import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useValidatedForm } from "@/components/app/FormKit";
import { btnOutline, btnPrimary } from "@/components/app/ui";
import {
  ACCOUNT_FIELDS, BUSINESS_FIELDS, STORE_FIELDS, STORE_DEFAULTS, ACCOUNT_DEFAULTS, AccountFields, BusinessFields, StoreFields,
  validateAccount, validateBusiness, validateStoreDetails, useStepValidator, useMeta, useSlugStatus, storePayload,
} from "@/components/onboarding/parts";

const STEP_FIELDS = { 1: ACCOUNT_FIELDS, 2: BUSINESS_FIELDS, 3: STORE_FIELDS };
const TITLES = { 1: "Your account", 2: "Your business", 3: "Your store" };

// Register mode of the shared store set-up journey (the Add New Store modal runs the same parts without step 1).
export default function Signup() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState(1);
  const meta = useMeta();
  const [loading, setLoading] = useState(false);

  const validate = useStepValidator([validateAccount, validateBusiness, validateStoreDetails]);

  const form = useValidatedForm({ ...ACCOUNT_DEFAULTS, ...STORE_DEFAULTS }, validate);
  const { values: f, bind } = form;

  const slugStatus = useSlugStatus(f.store_slug);

  const next = () => { if (form.check(STEP_FIELDS[step])) setStep((s) => s + 1); };

  const submit = async () => {
    if (!form.check(STEP_FIELDS[3])) return;
    if (slugStatus === "checking") return toast.info("Checking store URL availability… try again in a moment.");
    if (slugStatus === false) return toast.error("That store URL is taken — please pick another.");
    setLoading(true);
    try {
      const payload = { ...storePayload(f), name: f.name.trim(), email: f.email.trim(), mobile: f.mobile.replace(/[\s-]/g, ""), password: f.password };
      const { data } = await api.post("/auth/register", payload);
      login(data.token, data.user);
      toast.success("Store created successfully! Let's set it up.");
      nav("/onboarding");
    } catch (err) {
      const msg = apiErr(err.response?.data?.detail) || "Signup failed";
      toast.error(msg);
      if (/email/i.test(msg)) setStep(1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell wide>
      <div className="flex items-center gap-2 mb-4">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex-1 h-1.5 rounded-full overflow-hidden bg-slate-100">
            <div className={`h-full transition-all duration-500 ${s <= step ? "bg-blue-600" : ""}`} style={{ width: s <= step ? "100%" : "0%" }} />
          </div>
        ))}
      </div>
      <h1 className="font-display text-xl font-extrabold text-neutral-900">{TITLES[step]}</h1>
      <p className="text-slate-500 text-[13px] mt-0.5">Step <span className="text-orange-500 font-semibold">{step}</span> of 3 · fields marked * are required</p>

      <div className="mt-4 grid grid-cols-2 gap-x-3 gap-y-3">
        {step === 1 && <AccountFields bind={bind} />}
        {step === 2 && <BusinessFields bind={bind} meta={meta} />}
        {step === 3 && <StoreFields form={form} slugStatus={slugStatus} />}
      </div>

      <div className="flex items-center justify-between mt-5">
        {step > 1 ? (
          <button onClick={() => setStep((s) => s - 1)} data-testid="signup-back" className={btnOutline}><ArrowLeft size={14} /> Back</button>
        ) : <span />}
        {step < 3 ? (
          <button onClick={next} data-testid="signup-next" className={btnPrimary}>Continue <ArrowRight size={14} /></button>
        ) : (
          <button onClick={submit} disabled={loading} data-testid="signup-submit" className={btnPrimary}>{loading ? "Creating…" : "Create store"} <Check size={14} /></button>
        )}
      </div>
      <p className="text-[13px] text-slate-500 mt-4 text-center">
        Already have an account? <Link to="/login" className="text-orange-500 font-semibold hover:underline">Sign in</Link>
      </p>
    </AuthShell>
  );
}

